import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  PlayArrow as PlayIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
  AudioFile as AudioIcon,
  VideoFile as VideoIcon,
  Image as ImageIcon,
  FileUpload as ImportIcon,
} from '@mui/icons-material';
import attachmentsService, { Attachment } from '../services/attachmentsService';
import CSVImportModal from './CSVImportModal';

interface AttachmentsProps {
  claimId: string;
}

const Attachments: React.FC<AttachmentsProps> = ({ claimId }) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'view'>('list');
  const [selectedAttachment, setSelectedAttachment] = useState<(Attachment & { previewUrl?: string }) | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [loadingThumbnails, setLoadingThumbnails] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchAttachments();
  }, [claimId]);

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(thumbnails).forEach(url => {
        if (url.startsWith('blob:')) {
          window.URL.revokeObjectURL(url);
        }
      });
    };
  }, [thumbnails]);

  const fetchAttachments = async () => {
    try {
      setLoading(true);
      const data = await attachmentsService.getAttachments(claimId);
      setAttachments(data);
      
      // Load thumbnails for previewable files
      const newThumbnails: Record<string, string> = {};
      const newLoadingThumbnails: Record<string, boolean> = {};
      
      // Set loading state for all previewable files
      data.forEach(attachment => {
        if (attachmentsService.isPreviewable(attachment.mimeType)) {
          newLoadingThumbnails[attachment.id] = true;
        }
      });
      setLoadingThumbnails(newLoadingThumbnails);
      
      // Load thumbnails asynchronously
      for (const attachment of data) {
        if (attachmentsService.isPreviewable(attachment.mimeType)) {
          try {
            const thumbnailUrl = await attachmentsService.getAttachmentPreviewUrl(attachment.id);
            newThumbnails[attachment.id] = thumbnailUrl;
            setThumbnails(prev => ({ ...prev, [attachment.id]: thumbnailUrl }));
            setLoadingThumbnails(prev => ({ ...prev, [attachment.id]: false }));
          } catch (error) {
            console.error(`Failed to load thumbnail for ${attachment.originalName}:`, error);
            setLoadingThumbnails(prev => ({ ...prev, [attachment.id]: false }));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching attachments:', err);
      setError('Failed to load attachments');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    try {
      setUploading(true);
      await attachmentsService.uploadAttachment(claimId, selectedFiles);
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      await fetchAttachments(); // Refresh the list
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setError('Failed to upload attachments');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (attachmentId: string) => {
    try {
      await attachmentsService.downloadAttachment(attachmentId);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setError('Failed to download attachment');
    }
  };

  const handleDelete = async (attachmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      await attachmentsService.deleteAttachment(attachmentId);
      await fetchAttachments(); // Refresh the list
    } catch (err) {
      console.error('Error deleting attachment:', err);
      setError('Failed to delete attachment');
    }
  };

  const handleViewAttachment = async (attachment: Attachment) => {
    setSelectedAttachment(attachment);
    setViewerOpen(true);
    
    // Try to get preview URL for supported file types
    if (attachmentsService.isPreviewable(attachment.mimeType)) {
      try {
        const previewUrl = await attachmentsService.previewAttachment(attachment.id, attachment.mimeType);
        if (previewUrl) {
          setSelectedAttachment({ ...attachment, previewUrl });
        }
      } catch (error) {
        console.error('Failed to load preview:', error);
      }
    }
  };

  const getFileTypeIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return <ImageIcon />;
    if (mimeType.includes('pdf')) return <PdfIcon />;
    if (mimeType.startsWith('audio/')) return <AudioIcon />;
    if (mimeType.startsWith('video/')) return <VideoIcon />;
    if (mimeType.includes('document') || mimeType.includes('word') || 
        mimeType.includes('spreadsheet') || mimeType.includes('excel') ||
        mimeType.includes('presentation') || mimeType.includes('powerpoint')) {
      return <DocumentIcon />;
    }
    return <DocumentIcon />;
  };

  const canPreview = (mimeType: string) => {
    return mimeType.startsWith('image/') || 
           mimeType.includes('pdf') || 
           mimeType.startsWith('audio/') || 
           mimeType.startsWith('video/') ||
           mimeType.startsWith('text/');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderAttachmentPreview = (attachment: Attachment & { previewUrl?: string }) => {
    if (!attachment.previewUrl || !attachmentsService.isPreviewable(attachment.mimeType)) {
      // Fallback for non-previewable files
      return (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          {getFileTypeIcon(attachment.mimeType)}
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            {attachment.originalName}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Click download to view this file.
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(attachment.id)}
          >
            Download File
          </Button>
        </Box>
      );
    }

    // Handle different file types for preview
    if (attachment.mimeType.startsWith('image/')) {
      return (
        <Box sx={{ textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src={attachment.previewUrl}
            alt={attachment.originalName}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
          />
        </Box>
      );
    }

    if (attachment.mimeType === 'application/pdf') {
      return (
        <Box sx={{ height: '100%' }}>
          <iframe
            src={attachment.previewUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title={attachment.originalName}
          />
        </Box>
      );
    }

    if (attachment.mimeType.startsWith('audio/')) {
      return (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <AudioIcon sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            {attachment.originalName}
          </Typography>
          <audio controls style={{ width: '100%', maxWidth: 400 }}>
            <source src={attachment.previewUrl} type={attachment.mimeType} />
            Your browser does not support the audio element.
          </audio>
        </Box>
      );
    }

    if (attachment.mimeType.startsWith('video/')) {
      return (
        <Box sx={{ textAlign: 'center', p: 2 }}>
          <video
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '100%'
            }}
          >
            <source src={attachment.previewUrl} type={attachment.mimeType} />
            Your browser does not support the video element.
          </video>
        </Box>
      );
    }

    if (attachment.mimeType.startsWith('text/')) {
      return (
        <Box sx={{ height: '100%' }}>
          <iframe
            src={attachment.previewUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title={attachment.originalName}
          />
        </Box>
      );
    }

    // Fallback for other file types
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        {getFileTypeIcon(attachment.mimeType)}
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          {attachment.originalName}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Preview not available for this file type.
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => handleDownload(attachment.id)}
        >
          Download File
        </Button>
      </Box>
    );
  };

  const handleImportSuccess = (result: any) => {
    if (result.success) {
      // Show success message
      setError(null);
      // Refresh the attachments list
      fetchAttachments();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Attachments ({attachments.length})</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setImportModalOpen(true)}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload File
          </Button>
        </Box>
      </Box>

      {/* View Mode Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={viewMode} onChange={(_, newValue) => setViewMode(newValue)}>
          <Tab label="List View" value="list" />
          <Tab label="Gallery View" value="view" />
        </Tabs>
      </Box>

      {/* List View */}
      {viewMode === 'list' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Uploaded By</TableCell>
                <TableCell>Upload Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attachments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary">
                      No attachments found. Upload your first file to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                attachments.map((attachment) => (
                  <TableRow key={attachment.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getFileTypeIcon(attachment.mimeType)}
                        <Typography variant="body2">{attachment.originalName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attachment.mimeType}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {attachmentsService.formatFileSize(attachment.size)}
                    </TableCell>
                    <TableCell>
                      {attachment.creator.firstName} {attachment.creator.lastName}
                    </TableCell>
                    <TableCell>{formatDate(attachment.created_at)}</TableCell>
                    <TableCell align="right">
                      {canPreview(attachment.mimeType) && (
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleViewAttachment(attachment)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(attachment.id)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(attachment.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Gallery View */}
      {viewMode === 'view' && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {attachments.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', width: '100%' }}>
              <Typography color="textSecondary">
                No attachments found. Upload your first file to get started.
              </Typography>
            </Paper>
          ) : (
            attachments.map((attachment) => (
              <Box key={attachment.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 8px)', lg: 'calc(25% - 8px)' } }}>
                <Tooltip 
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {attachment.originalName}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Size: {attachmentsService.formatFileSize(attachment.size)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Type: {attachment.mimeType}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Uploaded: {formatDate(attachment.created_at)}
                      </Typography>
                      {attachmentsService.isPreviewable(attachment.mimeType) && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                          Click to preview
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}>
                  <Box sx={{ position: 'relative', height: 200, overflow: 'hidden', cursor: 'pointer' }}
                       onClick={() => handleViewAttachment(attachment)}>
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5'
                      }}
                    >
                      {loadingThumbnails[attachment.id] ? (
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgress size={40} />
                          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            Loading preview...
                          </Typography>
                        </Box>
                      ) : thumbnails[attachment.id] ? (
                        attachment.mimeType.startsWith('image/') ? (
                          <img
                            src={thumbnails[attachment.id]}
                            alt={attachment.originalName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : attachment.mimeType.includes('pdf') ? (
                          <Box sx={{ 
                            width: '100%', 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fff'
                          }}>
                            <iframe
                              src={thumbnails[attachment.id]}
                              style={{
                                width: '90%',
                                height: '90%',
                                border: 'none',
                                pointerEvents: 'none'
                              }}
                              title={`Preview of ${attachment.originalName}`}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ textAlign: 'center' }}>
                            {getFileTypeIcon(attachment.mimeType)}
                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                              Click to preview
                            </Typography>
                          </Box>
                        )
                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          {getFileTypeIcon(attachment.mimeType)}
                          {attachmentsService.isPreviewable(attachment.mimeType) && (
                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                              Click to preview
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                    
                    {/* Preview overlay for previewable files */}
                    {attachmentsService.isPreviewable(attachment.mimeType) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(0,0,0,0.6)',
                          borderRadius: '50%',
                          width: 48,
                          height: 48,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0,
                          transition: 'opacity 0.2s ease-in-out',
                          '.MuiCard-root:hover &': {
                            opacity: 1
                          }
                        }}
                      >
                        <ViewIcon sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                    )}
                    
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        gap: 1
                      }}
                    >
                      {canPreview(attachment.mimeType) && (
                        <IconButton
                          size="small"
                          sx={{ backgroundColor: 'rgba(255,255,255,0.8)' }}
                          onClick={() => handleViewAttachment(attachment)}
                        >
                          <ViewIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        sx={{ backgroundColor: 'rgba(255,255,255,0.8)' }}
                        onClick={() => handleDownload(attachment.id)}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" noWrap title={attachment.originalName}>
                      {attachment.originalName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {attachmentsService.formatFileSize(attachment.size)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {formatDate(attachment.created_at)}
                    </Typography>
                    {attachmentsService.isPreviewable(attachment.mimeType) && (
                      <Chip 
                        label="Previewable" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </CardContent>
                </Card>
                </Tooltip>
              </Box>
            ))
          )}
        </Box>
      )}

      {/* File Viewer Dialog */}
      <Dialog
        open={viewerOpen}
        onClose={() => {
          // Clean up blob URL to prevent memory leaks
          if (selectedAttachment?.previewUrl) {
            window.URL.revokeObjectURL(selectedAttachment.previewUrl);
          }
          setViewerOpen(false);
          setSelectedAttachment(null);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '80vh' } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedAttachment?.originalName}
            </Typography>
            <IconButton onClick={() => {
              // Clean up blob URL to prevent memory leaks
              if (selectedAttachment?.previewUrl) {
                window.URL.revokeObjectURL(selectedAttachment.previewUrl);
              }
              setViewerOpen(false);
              setSelectedAttachment(null);
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedAttachment && renderAttachmentPreview(selectedAttachment)}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => selectedAttachment && handleDownload(selectedAttachment.id)}
          >
            Download
          </Button>
          <Button onClick={() => {
            // Clean up blob URL to prevent memory leaks
            if (selectedAttachment?.previewUrl) {
              window.URL.revokeObjectURL(selectedAttachment.previewUrl);
            }
            setViewerOpen(false);
            setSelectedAttachment(null);
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Attachments</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            {/* Drag and Drop Area */}
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                border: `2px dashed ${dragOver ? '#1976d2' : '#e0e0e0'}`,
                borderRadius: 2,
                p: 3,
                mb: 2,
                textAlign: 'center',
                backgroundColor: dragOver ? '#f3f8ff' : '#fafafa',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onClick={() => document.getElementById('file-upload')?.click()}
            >
              <UploadIcon sx={{ fontSize: 48, color: dragOver ? '#1976d2' : '#9e9e9e', mb: 1 }} />
              <Typography variant="h6" color={dragOver ? 'primary' : 'textSecondary'}>
                {dragOver ? 'Drop files here' : 'Drag & drop files here'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                or click to browse files
              </Typography>
            </Box>

            <input
              type="file"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="file-upload"
              multiple
            />
            {selectedFiles.length > 0 && (
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Selected Files ({selectedFiles.length}):
                </Typography>
                {selectedFiles.map((file, index) => (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      p: 1,
                      mb: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f9f9f9',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {attachmentsService.formatFileSize(file.size)}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => removeFile(index)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Total Size: {attachmentsService.formatFileSize(
                    selectedFiles.reduce((total, file) => total + file.size, 0)
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setSelectedFiles([]);
          }}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={selectedFiles.length === 0 || uploading}
          >
            {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      <CSVImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        title="Import Attachments from CSV"
        endpoint="/attachments/import"
        sampleFileUrl="/api/uploads/attachments_sample.csv"
        onSuccess={handleImportSuccess}
      />
    </Box>
  );
};

export default Attachments; 